/**
 * InviteAccept — public landing page for accepting a tenant invite.
 *
 * Pulls preview (brand, role, inviter) from /api/tenants/invite/{token}.
 * On submit (name + password), POSTs /accept and lands the user on /dashboard
 * inside the inviter's tenant_id.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Mail, ShieldCheck, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import Logo from '@/components/Logo';

export default function InviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ name: '', password: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/tenants/invite/${token}`);
        setPreview(data);
        setForm((f) => ({ ...f, name: data.name || '' }));
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Invitation not found or expired');
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.password.length < 6) {
      toast.error('Name and a 6+ character password required.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/invite/${token}/accept`, form);
      // Cookie is set by backend; bring the in-memory bearer in sync too
      // so any axios call before the next page load has it.
      if (data?.token) {
        const { setToken } = await import('@/lib/tokenStore');
        setToken(data.token);
      }
      toast.success(`Welcome to ${preview.brand_name}.`);
      if (refresh) await refresh();
      nav('/dashboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not accept invite.');
    } finally { setBusy(false); }
  };

  if (err) {
    return (
      <main className="min-h-screen bg-[#F7F5F4] flex items-center justify-center px-6 py-20">
        <div className="ve-card p-10 max-w-md text-center">
          <AlertCircle size={36} className="mx-auto text-rose-500"/>
          <h1 className="display text-2xl mt-4 font-bold">Invitation unavailable</h1>
          <p className="text-sm text-slate-600 mt-2">{err}</p>
          <Link to="/login" className="mt-6 ve-btn-secondary inline-flex">Back to login</Link>
        </div>
      </main>
    );
  }

  if (!preview) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-[#F7F5F4]">
        <Loader2 className="animate-spin text-[#5E1F8B]" size={32}/>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F7F5F4]">
      <header className="h-16 flex items-center px-6 md:px-10 border-b border-slate-200 bg-white">
        <Logo size="sm"/>
      </header>

      <div className="max-w-xl mx-auto px-6 py-12 md:py-20">
        <div className="ve-eyebrow" style={{ color: preview.primary_color || '#5E1F8B' }}>You&apos;re invited</div>
        <h1 className="display text-3xl md:text-4xl mt-3 font-bold leading-tight">
          Join <span style={{ color: preview.primary_color || '#5E1F8B' }}>{preview.brand_name}</span> on Vigilant
        </h1>
        <p className="text-sm text-slate-600 mt-4 max-w-md">
          <b>{preview.invited_by_name}</b> has invited you to join as <b>{preview.role.replace(/_/g, ' ')}</b>.
          {preview.note && <span className="block italic mt-3">&ldquo;{preview.note}&rdquo;</span>}
        </p>

        <form onSubmit={submit} className="ve-card mt-10 p-7 md:p-10" data-testid="invite-accept-form">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Mail size={12}/> <span className="font-mono">{preview.email}</span>
          </div>
          <label className="mt-5 block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Your name
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="invite-name"
              className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal font-normal"
            />
          </label>
          <label className="mt-4 block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Create password (6+ chars)
            <input
              required
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="invite-password"
              className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 font-mono"
            />
          </label>

          <button
            type="submit"
            disabled={busy}
            data-testid="invite-accept-submit"
            className="mt-8 ve-btn-primary w-full justify-center"
          >
            <ShieldCheck size={14}/>
            {busy ? 'Joining…' : `Accept & join ${preview.brand_name}`}
            <ArrowRight size={14}/>
          </button>
          <p className="mt-4 text-[10px] text-slate-400 text-center">
            Already have an account? <Link to="/login" className="underline">Sign in</Link> — invites only work with the email above.
          </p>
        </form>
      </div>
    </main>
  );
}
